import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { StateUtils } from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { RecommendationPanel } from '../../model';
import { RecommendationsFacade } from '../facades/recommendations-facade';
import { UserActions } from '../store/actions';
import { getRecommendationPanelState } from '../store/selectors/recommendations.selectors';
import { StateWithUser } from '../store/user-state';
import { recommendationsEntityKeyBuilder } from '../store/util/recommendations-entity-key-builder';

export type PanelParameters = Record<string, string | number>;

@Injectable()
export class RecommendationsService implements RecommendationsFacade {
  constructor(private store: Store<StateWithUser>) {}

  getRecommendedProductsBasedOnProduct(productCode: string, parameters?: PanelParameters): Observable<RecommendationPanel> {
    return this.getPanel('products-by-product', { productCode, ...parameters });
  }

  getRecommendedProductsBasedOnArticle(articleCode: string, parameters?: PanelParameters): Observable<RecommendationPanel> {
    return this.getPanel('products-by-article', { articleCode, ...parameters });
  }

  getRecommendedArticlesBasedOnArticle(articleCode: string, parameters?: PanelParameters): Observable<RecommendationPanel> {
    return this.getPanel('articles-by-article', { articleCode, ...parameters });
  }

  getRecommendedProductsForUser(parameters?: PanelParameters): Observable<RecommendationPanel> {
    return this.getPanel('products-by-customer', parameters);
  }

  getRecommendedArticlesForUser(parameters?: PanelParameters): Observable<RecommendationPanel> {
    return this.getPanel('articles-by-customer', parameters);
  }

  getTopProducts(parameters?: PanelParameters): Observable<RecommendationPanel> {
    return this.getPanel('top-selling-products', parameters);
  }

  getTopArticles(parameters?: PanelParameters): Observable<RecommendationPanel> {
    return this.getPanel('top-selling-articles', parameters);
  }

  getPanel(panelName: string, parameters?: PanelParameters): Observable<RecommendationPanel> {
    const key = recommendationsEntityKeyBuilder(panelName, parameters?.productCode, parameters?.articleCode);

    return this.store.select(getRecommendationPanelState).pipe(
      tap((ps: StateUtils.EntityLoaderState<RecommendationPanel>) => {
        if (this.shouldLoad(ps, key)) {
          this.loadPanel(panelName, parameters);
        }
      }),
      filter((ps) => !!ps && !!ps.entities[key]?.value),
      map((ps: StateUtils.EntityLoaderState<RecommendationPanel>) => ps.entities[key].value),
      take(1)
    );
  }

  loadPanel(panelName: string, parameters?: PanelParameters): void {
    this.store.dispatch(
      new UserActions.LoadRecommendation({
        panelName,
        ...parameters,
      })
    );
  }

  private shouldLoad(state: StateUtils.EntityLoaderState<any>, key: string): boolean {
    const loaderState = state?.entities[key];
    return !loaderState || this.isInitialState(loaderState);
  }

  private isInitialState(loaderState: StateUtils.LoaderState<any>): boolean {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }
}
